'use client';

import { useEffect } from 'react';
import Env, {
	syncGetCurrentSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import { getRpcToUse } from 'src/utils/rpcUtils/rpcUtils_api';
import useLiquidationAlerts from './Liquidations/useLiquidationAlerts';
import useLiquidationWarningToasts from './Liquidations/useLiquidationWarningToasts';
import use24hMarketDataFromApi from './use24hMarketDataFromApi';
import useSyncBorrowLendBalances from './useSyncBorrowLendBalances';
import useClearStaleNotifications from './useClearStaleNotifications';
import { useKeepCurrentRpcHealthInSync } from './useCurrentRpcHealth';

import { useUpdateConnection } from './drift-actions/connection/useUpdateConnection';
import useDriftStatus from './useDriftStatus';
import useDriftMarketEventListener from './driftEvents/useDriftMarketEventListener';
import useGeolocation from './useGeolocation';
import useKeepWalletsInSync from './useKeepWalletsInSync';
import useMarketHistory from './useMarketHistory';
import usePredictedFunding from './usePredictedFunding';
import useSyncOracleDataWithRpc from './useSyncOracleDataWithRpc';
import useSolBalance from './useSolBalance';
import useSyncWithAllMarketsInfo from './useSyncWithAllMarketsInfo';
import useSyncLiquidationAlertState from './useSyncLiquidationAlertState';
import useUserStatsAccount from './useUserStatsAccount';
import useListenForNewDeploys from './useListenForNewDeploys';
import useIdlePollingRateSwitcher from './useIdlePollingSwitcher';
import useSyncSavedSubaccount from './useSyncSavedSubaccount';
import useCountConsoleErrors from './utils/useCountConsoleErrors';
import useDevInvariants from './dev/useDevInvariants';
import usePostHogGlobalEvents from './posthog/usePostHogGlobalEvents';
import useDocumentTitle from './useDocumentTitle';
import useAutomatedTxnsListener from './useAutomatedTxnsListener';
import useSyncMarketDataState from './dlob/useSyncMarketDataState';
import useWalletHooks from './useWalletHooks';
import { useHydrateDriftStoreFromLocalStorage } from './useHydrateDriftStoreFromLocalStorage';
import { useSyncAccountSpotBalances } from './useSyncAccountSpotBalances';
import useSyncPriorityFeeStore from '../providers/priorityFees/useSyncPriorityFeeStore';
import useOptimizedAccountLoader from './useOptimizedAccountLoader';
import useSyncAccountExists from './useSyncAccountExists';
import useSyncWindowSizeStore from './useSyncWindowSizeStore';
import useManageDriftEventSubscriptions from './driftEvents/useManageDriftEventSubscriptions';
import useDevActions from './useDevActions';
import useSelfFilling from './useSelfFilling';
import useSyncDriftClock from './useSyncChainClock';
import useSyncFeatureFlags from 'src/stores/featureFlag/useSyncFeatureFlags';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useAppEventEmitter from './useAppEventEmitter';
import usePostHogCapture from './posthog/usePostHogCapture';
import useDownloadRequests from './useDownloadRequests';
import useRouterConfig from './useRouterConfig';
import { useSyncTokenAccountsStore } from '../stores/tokenAccounts/useSyncTokenAccountsStore';
import useNavigationState from 'src/components/Navigation/hooks/useNavigationState';
import useSyncEventsSubscriptionState from './driftEvents/useSyncEventsSubscriptionState';
import { useSyncVaultClient } from 'src/stores/vaultsStore/useSyncVaultClient';
import { useSyncDepositInputStore } from './deposits/useSyncDepositInputStore';
import { useGlobalHotkeySetup } from './hotkeys/useGlobalHotkeySetup';
import useAlertNotificationListener from 'src/components/Toasts/AlertNotifications/hooks/useAlertNotificationListener';
import usePostHogSurveys from 'src/hooks/posthog/usePostHogSurveys';
import { useSlotSubscription } from 'src/hooks/subscriptions/useSlotSubscription';
import useDriftGlobalEventSubscribers from './driftEvents/useDriftGlobalEventSubscribers';
import { useVaultConfigs } from './query/vaults/base/useVaultConfigs';

import useSyncMarketSettings from './globalSyncs/marketSettings/useSyncMarketSettings';
import useFunXyzDepositWatcher from './useFunXyzDepositWatcher';
import useEmitPositionFillDeltas from './driftEvents/useEmitPositionFillDeltas';
import { useSyncPrivy } from './globalSyncs/wallet/useSyncPrivy';
import useCleanupLargeLocalStorage from './useCleanupLargeLocalStorage';
import useTopMakerAlertNotification from 'src/hooks/useTopMakerAlertNotification';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';

// NOTE: ^ move all global sync hooks to the /globalSyncs folder

/**
 * Nextjs is really annoying with its server-side rendering, and most of the app setup needs to happen in hooks, otherwise the server tries to access things like "window" which is only available in the browser.
 * @returns
 */
const useAppSetup = () => {
	// PERFORMANCE NOTE : THIS HOOK MAKE THE OUTER HOOK RENDER MUTLIPLE TIMES WHEN ON THE /dev/dev route, but not the trade page
	const get = useDriftStore((s) => s.get);
	const getAccountStore = useDriftAccountsStore((s) => s.get);
	const appEventEmitter = useAppEventEmitter();
	const { captureEvent } = usePostHogCapture();
	const { updateConnection } = useUpdateConnection();

	useEffect(() => {
		// @ts-ignore
		window.drift_dev = { getStore: get, getAccountStore: getAccountStore };
	}, []);

	// Initial setup of app-related things
	useEffect(() => {
		const rpcToUse = getRpcToUse();

		const usingDevnetOverride = syncGetCurrentSettings()?.devnetOverride;

		if (usingDevnetOverride) {
			notify({
				type: 'info',
				message: 'Using devnet settings',
			});
		}

		const currentSettings = syncGetCurrentSettings();

		if (!currentSettings) return;

		// initialize clearing house and connection
		updateConnection(
			rpcToUse,
			currentSettings.accountSubscriberType,
			Env.sdkEnv,
			'initializing'
		);
	}, []);

	useEffect(() => {
		TransactionErrorHandler.setAppEventEmitter(appEventEmitter);
		TransactionErrorHandler.setCaptureEvent(captureEvent);
	}, [appEventEmitter, captureEvent]);

	useRouterConfig();
	useHydrateDriftStoreFromLocalStorage();
	useSlotSubscription();
	useCleanupLargeLocalStorage();

	// These hooks now handle route-based optimization internally
	useListenForNewDeploys();
	useDriftMarketEventListener();
	use24hMarketDataFromApi();
	useMarketHistory();
	useSolBalance();
	useGeolocation();
	useDriftStatus();
	useSyncOracleDataWithRpc();
	useSyncBorrowLendBalances();
	usePredictedFunding();
	useLiquidationAlerts();
	useLiquidationWarningToasts();
	useKeepCurrentRpcHealthInSync();
	useKeepWalletsInSync();
	useSyncLiquidationAlertState();
	useUserStatsAccount();
	useIdlePollingRateSwitcher();
	useSyncSavedSubaccount();
	useCountConsoleErrors();
	useDevInvariants();
	usePostHogGlobalEvents();
	usePostHogSurveys();
	useDocumentTitle();
	useSyncPriorityFeeStore();
	useOptimizedAccountLoader();
	useAutomatedTxnsListener();
	useWalletHooks();
	useSyncAccountSpotBalances();
	useSyncAccountExists();
	useSyncWindowSizeStore();
	useManageDriftEventSubscriptions();
	useSelfFilling();
	useSyncFeatureFlags();
	useDownloadRequests();
	useSyncTokenAccountsStore();
	useNavigationState();
	useSyncEventsSubscriptionState();
	useSyncVaultClient();
	useDriftGlobalEventSubscribers();
	useVaultConfigs();
	useGlobalHotkeySetup();
	useSyncMarketSettings();
	useFunXyzDepositWatcher();

	/// Wallet
	useSyncPrivy();

	// PERFORMANCE NOTE : THESE HOOKS CAUSE THIS HOOK TO RUN ON A LOOP (on the trade page - for some reason it still loops on other pages though e.g. on route /dev/dev )
	useSyncMarketDataState();
	useSyncWithAllMarketsInfo();
	useSyncDriftClock();

	// Toasts / Notifications
	useClearStaleNotifications();
	useAlertNotificationListener();
	useEmitPositionFillDeltas();
	useTopMakerAlertNotification();

	// sync stuff after remote configs loaded
	useSyncDepositInputStore();

	// Dev Stuff
	useDevActions();

	return;
};

export default useAppSetup;
